import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    LayoutDashboard, Package, FolderTree, ShoppingCart, Users, Tag,
    Megaphone, Mail, LayoutTemplate, Truck, Settings, UserCog, ScrollText, LogOut, Menu, X, CreditCard,
} from "lucide-react";
import { api } from "../../lib/api";
import { Toaster } from "sonner";
import Stats from "./sections/Stats";
import ProductsAdmin from "./sections/ProductsAdmin";
import CategoriesAdmin from "./sections/CategoriesAdmin";
import OrdersAdmin from "./sections/OrdersAdmin";
import CustomersAdmin from "./sections/CustomersAdmin";
import DiscountsAdmin from "./sections/DiscountsAdmin";
import MarketingAdmin from "./sections/MarketingAdmin";
import EmailsAdmin from "./sections/EmailsAdmin";
import ContentAdmin from "./sections/ContentAdmin";
import IntegrationsAdmin from "./sections/IntegrationsAdmin";
import SettingsAdmin from "./sections/SettingsAdmin";
import StaffAdmin from "./sections/StaffAdmin";
import AuditAdmin from "./sections/AuditAdmin";

const SECTIONS = [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, perm: "orders", component: Stats },
    { key: "products", label: "Products", icon: Package, perm: "products", component: ProductsAdmin },
    { key: "categories", label: "Categories", icon: FolderTree, perm: "categories", component: CategoriesAdmin },
    { key: "orders", label: "Orders", icon: ShoppingCart, perm: "orders", component: OrdersAdmin },
    { key: "customers", label: "Customers", icon: Users, perm: "customers", component: CustomersAdmin },
    { key: "discounts", label: "Discounts", icon: Tag, perm: "discounts", component: DiscountsAdmin },
    { key: "marketing", label: "Marketing", icon: Megaphone, perm: "marketing", component: MarketingAdmin },
    { key: "emails", label: "Emails", icon: Mail, perm: "emails", component: EmailsAdmin },
    { key: "content", label: "Homepage Content", icon: LayoutTemplate, perm: "content", component: ContentAdmin },
    { key: "integrations", label: "Integrations", icon: CreditCard, perm: "settings", component: IntegrationsAdmin },
    { key: "delivery", label: "Delivery & Settings", icon: Truck, perm: "settings", component: SettingsAdmin },
    { key: "staff", label: "Staff & Roles", icon: UserCog, perm: "owner", component: StaffAdmin },
    { key: "audit", label: "Audit Log", icon: ScrollText, perm: "audit", component: AuditAdmin },
];

export default function AdminPortal() {
    const [user, setUser] = useState(undefined);
    const [active, setActive] = useState("dashboard");
    const [menuOpen, setMenuOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/auth/me")
            .then((r) => {
                if (r.data.role === "owner" || r.data.role === "staff") {
                    setUser(r.data);
                } else {
                    navigate("/admin/login", { replace: true });
                }
            })
            .catch(() => navigate("/admin/login", { replace: true }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (user === undefined)
        return (
            <main className="min-h-[60vh] flex items-center justify-center" data-testid="admin-loading">
                <p className="text-slate-500">Checking your session…</p>
            </main>
        );

    const allowed = SECTIONS.filter(
        (s) => user.role === "owner" || (s.perm !== "owner" && (user.permissions || []).includes(s.perm))
    );
    const current = allowed.find((s) => s.key === active) || allowed[0];
    const CurrentComponent = current?.component;

    const logout = async () => {
        await api.post("/auth/logout").catch(() => {});
        navigate("/admin/login", { replace: true });
    };

    return (
        <main data-testid="admin-portal" className="bg-slate-100 min-h-screen">
            <Toaster position="top-center" richColors />
            <div className="bg-[#0033A0] py-4 sticky top-0 z-40">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <button type="button" className="lg:hidden text-white p-1" onClick={() => setMenuOpen(!menuOpen)}
                            aria-label="Toggle admin menu" data-testid="admin-menu-toggle">
                            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                        </button>
                        <div>
                            <h1 className="font-heading text-xl md:text-2xl font-extrabold text-white" data-testid="admin-heading">
                                Freddie's Store Manager
                            </h1>
                            <p className="text-blue-200 text-xs" data-testid="admin-user">
                                {user.name} · {user.role === "owner" ? "Owner" : "Staff"}
                            </p>
                        </div>
                    </div>
                    <button type="button" onClick={logout} data-testid="admin-logout-button"
                        className="inline-flex items-center gap-2 rounded-full border border-white/60 px-4 py-2 text-sm font-bold text-white hover:bg-white hover:text-[#0033A0] transition-colors">
                        <LogOut className="h-4 w-4" /> Log out
                    </button>
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 lg:flex lg:gap-6">
                <nav
                    className={`${menuOpen ? "block" : "hidden"} lg:block lg:w-60 shrink-0 mb-6 lg:mb-0`}
                    data-testid="admin-sidebar"
                    aria-label="Admin sections"
                >
                    <ul className="bg-white rounded-2xl border border-slate-200 p-2 space-y-1 lg:sticky lg:top-24">
                        {allowed.map((s) => (
                            <li key={s.key}>
                                <button
                                    type="button"
                                    data-testid={`admin-nav-${s.key}`}
                                    onClick={() => {
                                        setActive(s.key);
                                        setMenuOpen(false);
                                    }}
                                    className={`w-full flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-heading font-bold transition-colors ${
                                        current?.key === s.key ? "bg-[#0033A0] text-white" : "text-slate-600 hover:bg-slate-100"
                                    }`}
                                >
                                    <s.icon className="h-4 w-4" />
                                    {s.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                </nav>
                <section className="flex-1 min-w-0" data-testid={`admin-section-${current?.key}`}>
                    {CurrentComponent && <CurrentComponent user={user} />}
                </section>
            </div>
        </main>
    );
}
